// The setup list's rules: a short honey-do list of its own over home, each
// item paying a few welcome points when it is done. Pure, so a node test
// covers every turn; setupStore.ts keeps the state on the device and
// SetupCard.tsx draws it.
//
// An item is done one of two ways. Most are done by the world: signed in,
// running from the Home Screen, notifications on, a first project of your
// own (observe(), below, marks them as soon as the device can tell, and a
// mark stays). The tour is done by finishing it. Any item can be skipped,
// the whole list can be put away ("Not now"), and Settings' "Replay the
// welcome" brings it back with every skip undone and every point kept. The
// list goes once every item it can offer here is done or skipped, after a
// small celebration.
//
// Welcome points live on this device beside the list's state. They are
// added to the status card's total and never sync: the server never hears
// of them, and the device store never holds them.

export const SETUP_ITEMS = ['tour', 'signin', 'passkey', 'homescreen', 'notify', 'project'] as const;
export type SetupItem = (typeof SETUP_ITEMS)[number];

/** What each item pays when it is done. Skipped pays nothing. */
export const SETUP_POINTS: Record<SetupItem, number> = { tour: 20, signin: 10, passkey: 10, homescreen: 10, notify: 10, project: 25 };

export type Mark = 'done' | 'skipped';
export type ItemStatus = Mark | 'open';

export interface SetupState {
  marks: Partial<Record<SetupItem, Mark>>;
  /** "Not now": the list is put away until Settings brings it back. */
  dismissed: boolean;
  /** The list was settled and has had its celebration: it stays away. */
  celebrated: boolean;
}

export const INITIAL: SetupState = Object.freeze({ marks: Object.freeze({}), dismissed: false, celebrated: false });

/** What the device can tell now, each fact the reason an item is done or cannot be offered here. */
export interface SetupFacts {
  signedIn: boolean;
  /** The account has a passkey; 'unsupported' where this browser cannot make one. */
  passkey: boolean | 'unsupported';
  /** Opened from the Home Screen (or the Dock), or installed from this tab just now. */
  standalone: boolean;
  /** Notifications on this device: on, or a state that says why not. */
  notify: 'on' | 'off' | 'blocked' | 'install-first' | 'unsupported';
  /** A project of the person's own exists on this device (not a demo, not the tour's). */
  ownProject: boolean;
}

/** The parent an item hangs under in the list: the passkey is the second half of signing in. */
export const PARENT: Partial<Record<SetupItem, SetupItem>> = { passkey: 'signin' };

export function status(state: SetupState, item: SetupItem): ItemStatus {
  return state.marks[item] ?? 'open';
}

/** Whether the item can be offered on this device at all. One already done always shows. */
export function offered(state: SetupState, item: SetupItem, facts: SetupFacts): boolean {
  if (state.marks[item] === 'done') return true;
  if (item === 'passkey') return facts.passkey !== 'unsupported';
  if (item === 'notify') return facts.notify !== 'unsupported';
  return true;
}

/** The items this device shows, in order. */
export function items(state: SetupState, facts: SetupFacts): SetupItem[] {
  return SETUP_ITEMS.filter((item) => offered(state, item, facts));
}

/** Whether the world has done this item: the facts say so. The tour has no fact; finishing it is the mark. */
export function doneByFacts(item: SetupItem, facts: SetupFacts): boolean {
  switch (item) {
    case 'tour': return false;
    case 'signin': return facts.signedIn;
    case 'passkey': return facts.signedIn && facts.passkey === true;
    case 'homescreen': return facts.standalone;
    case 'notify': return facts.notify === 'on';
    case 'project': return facts.ownProject;
  }
}

/**
 * The state with every item the facts have done marked done, a skip
 * included (doing it anyway beats having skipped it). The same object when
 * nothing changed, so a store can tell. A mark is never taken back: signing
 * out later does not unpay the points.
 */
export function observe(state: SetupState, facts: SetupFacts): SetupState {
  let marks = state.marks;
  for (const item of SETUP_ITEMS) {
    if (marks[item] !== 'done' && doneByFacts(item, facts)) marks = { ...marks, [item]: 'done' };
  }
  return marks === state.marks ? state : { ...state, marks };
}

export function mark(state: SetupState, item: SetupItem, as: Mark): SetupState {
  if (state.marks[item] === 'done' || state.marks[item] === as) return state;
  return { ...state, marks: { ...state.marks, [item]: as } };
}

/** Takes a skip back; a done item stays done. */
export function unskip(state: SetupState, item: SetupItem): SetupState {
  if (state.marks[item] !== 'skipped') return state;
  const marks = { ...state.marks };
  delete marks[item];
  return { ...state, marks };
}

export const dismiss = (state: SetupState): SetupState => (state.dismissed ? state : { ...state, dismissed: true });

/** Settings' "Replay the welcome": the list comes back, every skip undone, every point kept. */
export function replay(state: SetupState): SetupState {
  const marks: Partial<Record<SetupItem, Mark>> = {};
  for (const item of SETUP_ITEMS) if (state.marks[item] === 'done') marks[item] = 'done';
  return { marks, dismissed: false, celebrated: false };
}

export const celebrate = (state: SetupState): SetupState => (state.celebrated ? state : { ...state, celebrated: true });

/** The welcome points: what every done item paid. */
export function welcomePoints(state: SetupState): number {
  return SETUP_ITEMS.reduce((sum, item) => sum + (state.marks[item] === 'done' ? SETUP_POINTS[item] : 0), 0);
}

/** Every item this device offers is done or skipped. */
export function settled(state: SetupState, facts: SetupFacts): boolean {
  return items(state, facts).every((item) => status(state, item) !== 'open');
}

/** What the list is doing on home: showing, celebrating its last item, or away. */
export function phase(state: SetupState, facts: SetupFacts): 'list' | 'celebrate' | 'away' {
  if (state.dismissed || state.celebrated) return 'away';
  return settled(state, facts) ? 'celebrate' : 'list';
}

/** "2 of 6": items done, of the items offered here. */
export function tally(state: SetupState, facts: SetupFacts): { done: number; of: number } {
  const shown = items(state, facts);
  return { done: shown.filter((item) => status(state, item) === 'done').length, of: shown.length };
}

/** Reads a stored state, tolerating anything: a missing, old or hand-edited value is the initial state or the part of it that makes sense. */
export function parseState(raw: string | null): SetupState {
  if (!raw) return INITIAL;
  try {
    const data = JSON.parse(raw) as Partial<SetupState> | null;
    const marks: Partial<Record<SetupItem, Mark>> = {};
    for (const item of SETUP_ITEMS) {
      const m = (data?.marks as Record<string, unknown> | undefined)?.[item];
      if (m === 'done' || m === 'skipped') marks[item] = m;
    }
    return { marks, dismissed: data?.dismissed === true, celebrated: data?.celebrated === true };
  } catch {
    return INITIAL;
  }
}

/** Cents as the card says them: "$3" when whole, "$2.40" when not. */
export const credit = (cents: number): string => {
  const c = Math.max(0, Math.round(cents));
  return c % 100 === 0 ? `$${c / 100}` : `$${(c / 100).toFixed(2)}`;
};

/** The first project's line once signed in: the credit in view, and what it covers at today's estimate. Undefined when it covers no plan. */
export function creditLine(balanceCents: number, planCents: number): string | undefined {
  if (planCents <= 0) return undefined;
  const plans = Math.floor(balanceCents / planCents);
  if (plans < 1) return undefined;
  return `Your ${credit(balanceCents)} welcome credit covers about ${plans} ${plans === 1 ? 'plan' : 'plans'}.`;
}
